/*
 * Purpose: Inline secret fix — paste API keys when a connector needs credentials
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { ContextualError } from '@/components/errors/ContextualError';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { useSecretStore } from '@/stores/secretStore';
import type { ConnectorFixIssue } from '@/lib/connectorIssuePolicy';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';

interface SecretConnectorFixPanelProps {
  issue: ConnectorFixIssue;
  onFixed?: () => void;
}

export function SecretConnectorFixPanel({ issue, onFixed }: SecretConnectorFixPanelProps) {
  const secretName = issue.secretName ?? 'API_KEY';
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const setSecret = useSecretStore((s) => s.setSecret);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const val = draft.trim();
    if (!val) {
      setError('Paste the key value first.');
      return;
    }
    if (!isUnlocked) {
      setError('Unlock the vault above to save this key.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setSecret(secretName, val);
      setDraft('');
      toast.success(`${secretName} saved to vault`);
      onFixed?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {!isUnlocked && <VaultUnlockInline onUnlocked={onFixed} />}

      {error && (
        <ContextualError message={error} action={{ label: 'Dismiss', onClick: () => setError(null) }} />
      )}

      <div className="rounded-lg border border-border p-3 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{secretName}</span>
        </div>
        <div>
          <Label className="text-xs">Paste key value</Label>
          <Input
            type="password"
            autoComplete="off"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`sk-… or your ${secretName}`}
            className="text-xs font-mono mt-1"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void save()} disabled={busy || !isUnlocked}>
            Save to vault
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={REMEDIATION.builderSecrets}>Open Secrets panel</a>
          </Button>
          <Button size="sm" variant="ghost" asChild>
            <a href={REMEDIATION.setup}>Setup wizard</a>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SecretConnectorFixPanel;
